import { format } from "date-fns";

import { EdictEntryResponse } from "@/api";
import { RtfDataSourceColumn, buildRtfDataSource } from "@/common/components/mail-merge-access/utils/rtfDataSource";

import { FileableEdictEntry } from "./Edicts.types";

/** The name the OZUF mail merge main documents (Serienbrief-Hauptdokumente) open their data source under. */
export const OZUF_DATA_SOURCE_FILE_NAME = "ozuf_klienten.doc";

// Field names and order are what the OZUF templates merge on; widths (twips) and alignment are how the legacy
// data source file laid them out. All taken from that file.
const OZUF_DATA_SOURCE_COLUMNS = [
  { name: "edvnr", width: 1437 },
  { name: "schuldner", width: 1437 },
  { name: "inhalt", width: 6980 },
  { name: "gebdat", width: 1100, alignRight: true },
  { name: "strasse", width: 2870 },
  { name: "plz", width: 1437 },
  { name: "ort", width: 1437 },
  { name: "heute", width: 1437, alignRight: true },
] as const satisfies readonly RtfDataSourceColumn[];

type OzufDataSourceField = (typeof OZUF_DATA_SOURCE_COLUMNS)[number]["name"];

const DATA_SOURCE_DATE_FORMAT = "dd.MM.yyyy";

// The letter offers the counselling responsible for Vienna, so it only goes to people living there.
const OZUF_LETTER_CITY = "Wien";

// The labels of the OZUF text block (inhalt), worded as the legacy wrote them.
const OZUF_NOTICE_LABELS = {
  heading: "OZUF zu",
  published: "bekannt gemacht am:",
  name: "Name:",
  birthday: "GebDat:",
  address: "Adresse:",
} as const;

/**
 * Whether a row can be filed: it has to be matched to a client, carry a handle to fetch its edict text by,
 * and carry the court file number the case note is keyed on. A row missing any of the three has nothing to
 * file, so it carries no checkbox — leaving it selectable would let the button count rows that the action
 * would silently drop.
 */
export function isFileableEdict(entry: EdictEntryResponse): entry is FileableEdictEntry {
  return entry.clientId !== null && entry.sourceReference !== null && entry.fileNumber !== null;
}

/**
 * Whether an OZUF entry can receive the letter: it goes to people in Vienna the counselling does not know
 * yet. The city is compared ignoring case, as the legacy's Access query did.
 */
export function isOzufLetterRecipient(entry: EdictEntryResponse): boolean {
  return entry.clientId === null && entry.city?.localeCompare(OZUF_LETTER_CITY, "de", { sensitivity: "accent" }) === 0;
}

/**
 * One entry per person, so nobody receives the letter twice for several OZUFs, keeping the first entry's
 * data. A person is identified by date of birth and address as the legacy did, plus the name: the register
 * may leave the date of birth out, and two people without one at the same address must stay two letters.
 */
export function distinctOzufRecipients(entries: readonly EdictEntryResponse[]): EdictEntryResponse[] {
  const byPerson = new Map<string, EdictEntryResponse>();

  for (const entry of entries) {
    const key = [
      entry.lastName,
      entry.firstName,
      formatDataSourceDate(entry.birthday),
      entry.street,
      entry.postalCode,
      entry.city,
    ]
      .map((part) => (part ?? "").toLocaleLowerCase("de"))
      .join("|");

    if (!byPerson.has(key)) {
      byPerson.set(key, entry);
    }
  }

  return [...byPerson.values()];
}

/**
 * The OZUF data source: one row per recipient, in the field order the templates merge on. The case file
 * number (edvnr) stays empty, as a recipient by definition has no case file.
 *
 * Each row is built by field name and only then laid out in field order, so a reordered or renamed field
 * cannot shift values into the wrong column unnoticed.
 */
export function buildOzufDataSource(entries: readonly EdictEntryResponse[], today: Date): Uint8Array<ArrayBuffer> {
  const rows = entries.map((entry) => {
    const values: Record<OzufDataSourceField, string> = {
      edvnr: "",
      schuldner: `${entry.lastName} ${entry.firstName}`,
      inhalt: composeOzufNotice(entry),
      gebdat: formatDataSourceDate(entry.birthday),
      strasse: entry.street ?? "",
      plz: entry.postalCode ?? "",
      ort: entry.city ?? "",
      heute: formatDataSourceDate(today),
    };

    return OZUF_DATA_SOURCE_COLUMNS.map((column) => values[column.name]);
  });

  return buildRtfDataSource(OZUF_DATA_SOURCE_COLUMNS, rows);
}

/**
 * The OZUF text block (inhalt), laid out as the legacy did: a heading naming court and publication date,
 * the person as the register states them, then the register's own notice text.
 */
function composeOzufNotice(entry: EdictEntryResponse): string {
  return [
    `${OZUF_NOTICE_LABELS.heading} ${entry.court ?? ""}`,
    `${OZUF_NOTICE_LABELS.published} ${formatDataSourceDate(entry.publishedDate)}`,
    "",
    `${OZUF_NOTICE_LABELS.name} ${entry.firstName} ${entry.lastName}`,
    `${OZUF_NOTICE_LABELS.birthday} ${formatDataSourceDate(entry.birthday)}`,
    `${OZUF_NOTICE_LABELS.address} ${entry.address ?? ""}`,
    "",
    entry.content ?? "",
  ].join("\n");
}

function formatDataSourceDate(date: Date | null): string {
  return date === null ? "" : format(date, DATA_SOURCE_DATE_FORMAT);
}
